import { api } from './api';
import type { AnalyticsEventInput } from '@tg-hookah/shared/dto';

let queue: AnalyticsEventInput[] = [];
let timer: number | null = null;

function flush(): void {
  if (queue.length === 0) return;
  const batch = queue.slice(0, 50);
  queue = queue.slice(50);
  // Fire-and-forget; analytics must never break the app.
  void api.post('/events', { events: batch }).catch(() => undefined);
  if (queue.length > 0) schedule();
}

function schedule(): void {
  if (timer != null) return;
  timer = window.setTimeout(() => {
    timer = null;
    flush();
  }, 2000);
}

const MAX_QUEUE = 500;

export function track(ev: AnalyticsEventInput): void {
  queue.push(ev);
  // Bound memory if the server is unreachable for a long session: keep newest.
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  if (queue.length >= 20) flush();
  else schedule();
}

/** Map a router path to a stable screen name for funnels. */
export function routeName(path: string): string {
  if (path === '/' || path === '') return 'home';
  if (path.startsWith('/booking')) return 'booking';
  if (path.startsWith('/profile')) return 'profile';
  if (path.startsWith('/menu')) return 'menu';
  if (path.startsWith('/contacts')) return 'contacts';
  if (path.startsWith('/director')) return 'director';
  if (path.startsWith('/albums')) return 'albums';
  if (path.startsWith('/faq')) return 'faq';
  if (path.startsWith('/page/')) return 'page';
  if (path.startsWith('/legal/')) return 'legal';
  return path.replace(/^\//, '').split('/')[0] || 'home';
}

export function trackScreen(path: string): void {
  track({ type: 'SCREEN_VIEW', name: `screen:${routeName(path)}`, path });
}

export function trackClick(
  name: string,
  path: string,
  coords?: { x: number; y: number; vw: number; vh: number },
): void {
  track({ type: 'CLICK', name, path, ...coords });
}

/** Flush any buffered events when the user leaves/backgrounds the app. */
export function installFlushOnHide(): () => void {
  const handler = () => flush();
  document.addEventListener('visibilitychange', handler);
  window.addEventListener('pagehide', handler);
  return () => {
    document.removeEventListener('visibilitychange', handler);
    window.removeEventListener('pagehide', handler);
  };
}
